import React, { memo } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { CELL_WIDTH } from '../../utils/gridConstants';

const GridCellStyled = styled.div`
  width: ${CELL_WIDTH}px;
  min-width: ${CELL_WIDTH}px;
  flex-shrink: 0;
  border-right: 1px solid #eeeeee;
  border-top: 1px solid transparent;
  border-bottom: 1px solid transparent;
  border-left: 1px solid transparent;
  position: relative;
  cursor: ${(props) => (props.hasBlock ? 'default' : 'pointer')};
  background-color: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px 8px;
  box-sizing: border-box;
  transform: translateZ(0); /* GPU acceleration for grid cells */
  will-change: ${(props) => (props.isDragging ? 'background-color, border-color' : 'auto')};

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background: ${(props) => (
    props.isDragging
        || props.hasBlock
        || props.isEditing
        || props.hasSelection
      ? 'transparent'
      : 'rgba(119, 167, 255, 0.2)')};
      border: ${(props) => (
    props.isDragging
        || props.hasBlock
        || props.isEditing
        || props.hasSelection
      ? '1px solid transparent'
      : '1px solid #77A7FF')};
      border-right: ${(props) => (
    props.isDragging
        || props.hasBlock
        || props.isEditing
        || props.hasSelection
      ? '1px solid #EEEEEE'
      : '1px solid #77A7FF')};
    }

    &:hover::after {
      opacity: ${(props) => (
    props.isDragging
        || props.hasBlock
        || props.isEditing
        || props.hasSelection
      ? '0'
      : '1')};
    }
  }

  @media (hover: none) {
    &:active {
      background: transparent;
      border-right: 1px solid #eeeeee;
      border-top: 1px solid transparent;
      border-bottom: 1px solid transparent;
      border-left: 1px solid transparent;
    }
  }

  &::after {
    content: '+';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 24px;
    font-weight: 400;
    color: #77a7ff;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.15s ease;
  }
`;

// Memoized wrapper for GridCell to prevent unnecessary re-renders
// Only re-renders when its specific props change, not when parent re-renders
const GridCell = memo(({
  week,
  rowNum,
  isDragging,
  hasBlock,
  isEditing,
  hasSelection,
  onCellClick,
}) => (
  <GridCellStyled
    isDragging={isDragging}
    hasBlock={hasBlock}
    isEditing={isEditing}
    hasSelection={hasSelection}
    onClick={(e) => {
      e.stopPropagation();
      onCellClick(week, rowNum);
    }}
  />
));

GridCell.displayName = 'GridCell';

GridCell.propTypes = {
  week: PropTypes.string.isRequired,
  rowNum: PropTypes.number.isRequired,
  isDragging: PropTypes.bool,
  hasBlock: PropTypes.bool,
  isEditing: PropTypes.bool,
  hasSelection: PropTypes.bool,
  onCellClick: PropTypes.func.isRequired,
};

GridCell.defaultProps = {
  isDragging: false,
  hasBlock: false,
  isEditing: false,
  hasSelection: false,
};

export default GridCell;
